import type { Post } from "@/types";
import { formatBodyAsPlainText, getPostPath } from "@/utils/postUtils";

/**
 * Check if the current device is a mobile device
 * Uses user agent detection for reliable mobile detection
 * Explicitly excludes Windows desktop (even if navigator.share is available)
 */
export function isMobileDevice(): boolean {
  const ua = navigator.userAgent;
  // Explicitly exclude Windows desktop
  if (/Windows/i.test(ua) && !/Windows Phone/i.test(ua)) {
    return false;
  }
  // Check for mobile devices
  return /iPhone|iPad|iPod|Android|webOS|BlackBerry|IEMobile|Opera Mini/i.test(
    ua,
  );
}

/**
 * Get the brand name from the current hostname
 * Maps domains to their brand names for share text
 */
function getBrandName(): string {
  const hostname = window.location.hostname;

  // Map known domains to brand names
  if (hostname.includes("gunclub")) return "Gunclub";
  if (hostname.includes("unbounded")) return "Unbounded";

  // Default: capitalize first part of hostname
  const parts = hostname.split(".");
  const name = parts[0] === "app" || parts[0] === "www" ? parts[1] : parts[0];
  return name ? name.charAt(0).toUpperCase() + name.slice(1) : "this app";
}

export type ShareResult = "success" | "cancelled" | "error";

function buildShareUrl(
  post: Post,
  baseUrl?: string,
  referralCode?: string | null,
): string {
  const base = baseUrl || window.location.origin;
  const path = getPostPath(post);
  let url = `${base}${path}`;
  if (referralCode) {
    const sep = path.includes("?") ? "&" : "?";
    url += `${sep}ref=${encodeURIComponent(referralCode)}`;
  }
  return url;
}

/**
 * Share a post using the Web Share API or clipboard fallback
 * @param post - The post to share
 * @param baseUrl - The base URL of the application (e.g., window.location.origin)
 * @param referralCode - Optional referral code to append as ?ref= parameter
 * @returns Promise<ShareResult> - 'success' if shared, 'cancelled' if user cancelled, 'error' if failed
 */
export async function sharePost(
  post: Post,
  baseUrl?: string,
  referralCode?: string | null,
): Promise<ShareResult> {
  const postId = post.ulid || post.id;
  if (!postId) {
    console.warn("Cannot share: post ID not available");
    return "error";
  }

  const url = buildShareUrl(post, baseUrl, referralCode);

  const postBodyRaw =
    post.content || (typeof post.body === "string" ? post.body : "") || "";
  const postBody = formatBodyAsPlainText(postBodyRaw);
  const brandName = getBrandName();

  // For songs, use artist name or fall back to username from post data
  const artistName =
    post.artist ||
    ((post as Record<string, unknown>).username as string | undefined);

  // Generate share title (may be ignored by some share targets)
  const title =
    post.type === "SONG"
      ? `${post.title || "Song"}${artistName ? ` by ${artistName}` : ""}`
      : postBody.substring(0, 60) || "Check out this post";

  // Generate share text with brand promotion
  // This appears as the message body in share dialogs (e.g., iMessage bubble)
  // OG tags handle the link preview, so use text for call-to-action
  const text =
    post.type === "SONG"
      ? `Listen to "${post.title || "this track"}" on ${brandName}`
      : postBody || `Check this out on ${brandName}`;

  try {
    // Use Web Share API ONLY on mobile devices (iPhone, iPad, Android)
    // NEVER use Windows Share API on desktop - always copy to clipboard instead
    // Even if navigator.share is available on Windows desktop, we skip it
    if (isMobileDevice() && navigator.share) {
      try {
        await navigator.share({
          title,
          text,
          url,
        });
        return "success";
      } catch (error) {
        if (error instanceof Error && error.name === "AbortError") {
          return "cancelled";
        }
        const copied = await copyToClipboard(url);
        if (copied) {
          return "success";
        }
        console.error("Failed to share:", error);
        return "error";
      }
    }

    // Desktop (including Windows): Always copy to clipboard
    const copied = await copyToClipboard(url);
    return copied ? "success" : "error";
  } catch (error) {
    // User cancelled or error occurred
    console.error("Failed to share:", error);
    return "error";
  }
}

/**
 * Share an arbitrary URL (native sheet on mobile, clipboard on desktop).
 * Use when you do not have a full {@link Post} (e.g. comment list on post detail).
 */
export async function shareTextUrl(options: {
  url: string;
  title?: string;
  text?: string;
}): Promise<ShareResult> {
  const {
    url,
    title = "Share",
    text = `Check this out on ${getBrandName()}`,
  } = options;
  if (!url) {
    return "error";
  }
  try {
    if (isMobileDevice() && navigator.share) {
      await navigator.share({ title, text, url });
      return "success";
    }
    const copied = await copyToClipboard(url);
    return copied ? "success" : "error";
  } catch (error) {
    if (error instanceof Error && error.name === "AbortError") {
      return "cancelled";
    }
    console.error("Failed to share URL:", error);
    return "error";
  }
}

/**
 * Copy text to clipboard
 * @param text - Text to copy
 * @returns Promise<boolean> - true if copied successfully
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    // Try modern clipboard API first
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }

    // Fallback for older browsers
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.left = "-999999px";
    textArea.style.top = "-999999px";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    const successful = document.execCommand("copy");
    document.body.removeChild(textArea);

    return successful;
  } catch (error) {
    console.error("Failed to copy to clipboard:", error);
    return false;
  }
}

/**
 * Get shareable URL for a post
 * @param post - The post
 * @param baseUrl - The base URL of the application
 * @param referralCode - Optional referral code to append as ?ref= parameter
 * @returns string - The shareable URL
 */
export function getPostShareUrl(
  post: Post,
  baseUrl?: string,
  referralCode?: string | null,
): string {
  const postId = post.ulid || post.id;
  if (!postId) return "";

  return buildShareUrl(post, baseUrl, referralCode);
}
